// Task #3972 — My Profile → Notifications: the unified "Assignment
// notifications" matrix. Four assignment event types × three opt-in channels
// (Email / SMS / Push), an "Enable browser push on this device" affordance, and
// a verify-phone flow that gates the SMS column. In-app notifications are always
// on (surfaced as a note, never a toggle). Prefs persist via
// PATCH /api/app/profile { assignmentNotificationPrefs }.
import { useState } from "react";
import { useQueryClient, useMutation } from "@tanstack/react-query";
import { apiRequest, parseErrorMessage } from "@/lib/queryClient";
import { useToast } from "@/hooks/use-toast";
import { Switch } from "@/components/ui/switch";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Badge } from "@/components/ui/badge";
import { Loader2, BellRing, ShieldCheck, Smartphone } from "lucide-react";
import { usePushSubscription } from "@/hooks/use-push-subscription";
import {
  ASSIGNMENT_NOTIFICATION_TYPES,
  resolveAssignmentNotificationPrefs,
  type AssignmentNotificationType,
  type AssignmentNotificationChannel,
  type AssignmentNotificationPrefs,
} from "@shared/schema";

type Props = {
  prefs: unknown;
  phone: string | null;
  phoneVerified: boolean;
};

const TYPE_LABELS: Record<AssignmentNotificationType, { title: string; hint: string }> = {
  team_inbox_event: {
    title: "Team Inbox alerts",
    hint: "Unassigned alerts go to the account owner; assigned alerts go to the authorized assignee",
  },
  mention: {
    title: "Mentions",
    hint: "When a teammate @-mentions you in a comment or thread",
  },
  task_assigned: {
    title: "Maintenance task assigned",
    hint: "When a maintenance task is assigned to you",
  },
  todo_assigned: {
    title: "To-do assigned",
    hint: "When a to-do is assigned to you",
  },
  member_message_assigned: {
    title: "Member message assigned",
    hint: "When a member conversation is routed to you",
  },
  member_message_received: {
    title: "New member message",
    hint: "When a member sends a new message in a conversation you can access (push notifies the mobile app)",
  },
};

// Channels with no delivery path for a given type render a disabled switch:
// per-message email volume is unbounded and no template exists, so the email
// column is locked for `member_message_received` (the pref also defaults off).
const UNAVAILABLE_CHANNELS: Partial<Record<AssignmentNotificationType, AssignmentNotificationChannel[]>> = {
  team_inbox_event: ["email", "sms"],
  member_message_received: ["email"],
};

const CHANNELS: { id: AssignmentNotificationChannel; label: string }[] = [
  { id: "email", label: "Email" },
  { id: "sms", label: "SMS" },
  { id: "push", label: "Push" },
];

export function AssignmentNotificationsSection({ prefs: rawPrefs, phone, phoneVerified }: Props) {
  const queryClient = useQueryClient();
  const { toast } = useToast();
  const push = usePushSubscription();
  const [prefs, setPrefs] = useState<AssignmentNotificationPrefs>(() =>
    resolveAssignmentNotificationPrefs(rawPrefs),
  );
  const [phoneInput, setPhoneInput] = useState(phone ?? "");
  const [code, setCode] = useState("");
  const [codeSent, setCodeSent] = useState(false);

  const prefsMutation = useMutation({
    mutationFn: async (vars: { next: AssignmentNotificationPrefs; prev: AssignmentNotificationPrefs }) => {
      const res = await apiRequest("PATCH", "/api/app/profile", {
        assignmentNotificationPrefs: vars.next,
      });
      return res.json();
    },
    onSuccess: (data) => {
      queryClient.invalidateQueries({ queryKey: ["/api/app/profile"] });
      if (data?.assignmentNotificationPrefs) {
        setPrefs(resolveAssignmentNotificationPrefs(data.assignmentNotificationPrefs));
      }
    },
    onError: (err: Error, vars) => {
      setPrefs(vars.prev);
      toast({
        title: "Could not update notification preference",
        description: parseErrorMessage(err, "Please try again."),
        variant: "destructive",
      });
    },
  });

  function setChannel(
    type: AssignmentNotificationType,
    channel: AssignmentNotificationChannel,
    value: boolean,
  ) {
    const prev = prefs;
    const next: AssignmentNotificationPrefs = {
      ...prev,
      [type]: { ...prev[type], [channel]: value },
    };
    setPrefs(next);
    prefsMutation.mutate({ next, prev });
  }

  const sendCodeMutation = useMutation({
    mutationFn: async () => {
      const res = await apiRequest(
        "POST",
        "/api/app/phone/send-code",
        phoneInput.trim() ? { phone: phoneInput.trim() } : {},
      );
      return res.json();
    },
    onSuccess: () => {
      setCodeSent(true);
      toast({
        title: "Verification code sent",
        description: "Enter the 6-digit code we just texted you.",
      });
    },
    onError: (err: Error) => {
      toast({
        title: "Could not send code",
        description: parseErrorMessage(err, "Please try again."),
        variant: "destructive",
      });
    },
  });

  const verifyMutation = useMutation({
    mutationFn: async () => {
      const res = await apiRequest("POST", "/api/app/phone/verify", {
        phone: phoneInput.trim() || undefined,
        code: code.trim(),
      });
      return res.json();
    },
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ["/api/app/profile"] });
      setCode("");
      setCodeSent(false);
      toast({
        title: "Phone verified",
        description: "SMS notifications are now available.",
      });
    },
    onError: (err: Error) => {
      toast({
        title: "Could not verify code",
        description: parseErrorMessage(err, "Please try again."),
        variant: "destructive",
      });
    },
  });

  const pushStatusText = !push.supported
    ? "This browser doesn't support push notifications."
    : push.configured === false
      ? "Push isn't configured on the server yet."
      : push.subscribed
        ? "Push is enabled on this device."
        : push.permission === "denied"
          ? "Notifications are blocked in your browser settings."
          : "Enable to receive push notifications on this device.";

  return (
    <div className="mt-10" data-testid="assignment-notifications-root">
      <h2
        className="text-lg font-semibold text-foreground"
        data-testid="text-assignment-notifications-title"
      >
        Assignment notifications
      </h2>
      <p className="text-sm text-muted-foreground mt-1">
        When you're @-mentioned or something is assigned to you, choose how PadPulse reaches you.
        In-app notifications are always on.
      </p>

      {/* Browser push enablement (per device). */}
      <div className="mt-6 rounded-lg border border-border p-5 flex items-start justify-between gap-4">
        <div className="min-w-0">
          <p className="text-sm font-medium text-foreground flex items-center gap-2">
            <BellRing className="h-4 w-4" /> Browser push on this device
          </p>
          <p className="text-xs text-muted-foreground mt-1" data-testid="text-push-status">
            {pushStatusText}
          </p>
          {push.error ? (
            <p className="text-xs text-red-600 mt-1" data-testid="text-push-error">
              {push.error}
            </p>
          ) : null}
        </div>
        {push.supported && push.configured !== false ? (
          push.subscribed ? (
            <Button
              variant="outline"
              size="sm"
              disabled={push.busy}
              onClick={() => void push.disable()}
              data-testid="button-disable-push"
            >
              {push.busy ? <Loader2 className="h-4 w-4 animate-spin" /> : "Disable"}
            </Button>
          ) : (
            <Button
              size="sm"
              disabled={push.busy || push.configured === null}
              onClick={() => void push.enable()}
              data-testid="button-enable-push"
            >
              {push.busy ? <Loader2 className="h-4 w-4 animate-spin" /> : "Enable"}
            </Button>
          )
        ) : null}
      </div>

      {/* Verify-phone affordance — gates the SMS column. */}
      {phoneVerified ? (
        <div className="mt-4 rounded-lg border border-border p-5 flex items-center justify-between gap-4">
          <div className="min-w-0">
            <p className="text-sm font-medium text-foreground flex items-center gap-2">
              <ShieldCheck className="h-4 w-4 text-green-600" /> Phone verified
            </p>
            <p className="text-xs text-muted-foreground mt-1">
              SMS notifications can be enabled per event type below.
            </p>
          </div>
          <Badge variant="secondary" data-testid="badge-phone-verified">
            Verified
          </Badge>
        </div>
      ) : (
        <div className="mt-4 rounded-lg border border-border p-5 space-y-3">
          <div className="min-w-0">
            <p className="text-sm font-medium text-foreground flex items-center gap-2">
              <Smartphone className="h-4 w-4" /> Verify your phone to enable SMS
            </p>
            <p className="text-xs text-muted-foreground mt-1" data-testid="text-sms-needs-verify">
              The SMS column stays locked until you verify a mobile number. Standard message rates may
              apply.
            </p>
          </div>
          <div className="flex flex-col sm:flex-row gap-2">
            <Input
              value={phoneInput}
              onChange={(e) => setPhoneInput(e.target.value)}
              placeholder="+14155551234"
              className="sm:max-w-[200px]"
              data-testid="input-verify-phone"
            />
            <Button
              variant="outline"
              size="sm"
              disabled={sendCodeMutation.isPending}
              onClick={() => sendCodeMutation.mutate()}
              data-testid="button-send-phone-code"
            >
              {sendCodeMutation.isPending ? (
                <Loader2 className="h-4 w-4 animate-spin" />
              ) : codeSent ? (
                "Resend code"
              ) : (
                "Send code"
              )}
            </Button>
          </div>
          {codeSent ? (
            <div className="flex flex-col sm:flex-row gap-2">
              <Input
                value={code}
                onChange={(e) => setCode(e.target.value)}
                placeholder="6-digit code"
                inputMode="numeric"
                className="sm:max-w-[200px]"
                data-testid="input-phone-code"
              />
              <Button
                size="sm"
                disabled={verifyMutation.isPending || !code.trim()}
                onClick={() => verifyMutation.mutate()}
                data-testid="button-verify-phone-code"
              >
                {verifyMutation.isPending ? <Loader2 className="h-4 w-4 animate-spin" /> : "Verify"}
              </Button>
            </div>
          ) : null}
        </div>
      )}

      {/* The 4 × 3 channel matrix. */}
      <div
        className="mt-4 rounded-lg border border-border divide-y divide-border"
        data-testid="section-assignment-notifications"
      >
        <div className="hidden sm:grid grid-cols-[1fr_180px] gap-4 px-5 py-3 bg-muted/40">
          <span className="text-xs font-medium text-muted-foreground">Event</span>
          <div className="grid grid-cols-3 gap-2 text-center text-xs font-medium text-muted-foreground">
            {CHANNELS.map((c) => (
              <span key={c.id}>{c.label}</span>
            ))}
          </div>
        </div>
        {ASSIGNMENT_NOTIFICATION_TYPES.map((type) => (
          <div
            key={type}
            className="grid grid-cols-1 sm:grid-cols-[1fr_180px] gap-3 sm:gap-4 px-5 py-4 items-center"
          >
            <div className="min-w-0">
              <p className="text-sm font-medium text-foreground">{TYPE_LABELS[type].title}</p>
              <p className="text-xs text-muted-foreground mt-0.5">{TYPE_LABELS[type].hint}</p>
            </div>
            <div className="grid grid-cols-3 gap-2">
              {CHANNELS.map((c) => {
                const smsLocked = c.id === "sms" && !phoneVerified;
                const channelUnavailable = UNAVAILABLE_CHANNELS[type]?.includes(c.id) ?? false;
                const disabled = smsLocked || channelUnavailable || prefsMutation.isPending;
                return (
                  <div key={c.id} className="flex flex-col items-center gap-1">
                    <span className="text-[10px] text-muted-foreground sm:hidden">{c.label}</span>
                    <Switch
                      checked={prefs[type][c.id]}
                      disabled={disabled}
                      onCheckedChange={(v) => setChannel(type, c.id, v)}
                      aria-label={`${TYPE_LABELS[type].title} — ${c.label}`}
                      data-testid={`switch-assignment-${type}-${c.id}`}
                    />
                  </div>
                );
              })}
            </div>
          </div>
        ))}
      </div>
    </div>
  );
}
